import { useMemo } from 'react'
import { useInviteRolesQuery } from '../../api/user-invitation-api-client'

import { isInviteAllowedRbacRoleName } from './inviteRbacRoles'

export interface RbacRoleOption {
  label: string
  value: string
}

/**
 * Active RBAC roles as Select options (label = role name, value = role id).
 * Sourced from the dedicated invite-roles endpoint (GET /users/invite-roles)
 * so the finance flow gets the real, current invite-allowed roles without
 * needing the RBAC-Admin-only access-control roles endpoint.
 *
 * v1: only Finance Manager is assignable via invite. The endpoint already
 * scopes to active invite-allowed roles; the filter below is a defensive
 * double-check that keeps the same guarantee if the endpoint ever widens.
 */
export const useRbacRoleOptions = (): {
  options: RbacRoleOption[]
  isLoading: boolean
  isError: boolean
} => {
  const { data, isLoading, isError } = useInviteRolesQuery()

  const options = useMemo<RbacRoleOption[]>(
    () =>
      (data ?? [])
        .filter(
          (role) =>
            role.status === 'active' && isInviteAllowedRbacRoleName(role.name)
        )
        .map((role) => ({ label: role.name, value: role.id })),
    [data]
  )

  return { options, isLoading, isError }
}
